<script setup>
import { computed, useAttrs } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();

const store = useStore();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const wavoipState = computed(() => store.state.webphone?.wavoip || {});

// Check if WaVoIP feature is enabled
const isWavoipFeatureEnabled = computed(() => {
  return isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.WAVOIP
  );
});

// Check if user is an agent for this inbox
const assignableAgents = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  if (!inboxId) return [];
  return store.getters['inboxAssignableAgents/getAssignableAgents'](inboxId);
});

const isAgentForInbox = computed(() => {
  const userId = currentUser.value?.id;
  return assignableAgents.value.some(agent => agent.id === userId);
});

// Unified behavior: show when phone + feature + wavoip + agent
const shouldRender = computed(() => {
  return (
    !!props.phone &&
    isWavoipFeatureEnabled.value &&
    Object.keys(wavoipState.value).length > 0 &&
    isAgentForInbox.value
  );
});

const onClick = () => {
  store.dispatch('webphone/outcomingCall', {
    phone: props.phone,
    contact_name: '',
    chat_id: currentChat.value?.id,
    profile_picture: '',
  });
};
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltipLabel || null"
      v-bind="attrs"
      :label="label"
      :icon="icon"
      :size="size"
      @click="onClick"
    />
  </span>
</template>
